import { Directive, ElementRef, HostListener, Input } from "@angular/core";
import { NgControl } from "@angular/forms";

@Directive({
    selector: '[inputCurrency]',
})
export class InputCurrencyDirective {

    @Input() decimal: number = 2;
    @Input() style: 'decimal' | 'percent' = 'decimal';
    private el: HTMLInputElement;

    constructor(private elementRef: ElementRef, private ngControl: NgControl) {
        this.el = this.elementRef.nativeElement;
    }

    ngOnInit() {
        // Format initial value if already populated
        const initialValue = this.ngControl ? this.ngControl.control.value : null;
        if (initialValue !== null && initialValue !== undefined && initialValue !== '') {
            this.el.value = this.formatValue(parseFloat(initialValue));
        }

        // Listen for programmatic value changes (patchValue, setValue)
        this.ngControl.control.valueChanges.subscribe(value => {
            if (document.activeElement !== this.el) {
                // Only format if input is not focused (user is not typing)
                const raw = this.parseValue(String(value ? value : ''));
                this.el.value = isNaN(raw) ? '' : this.formatValue(raw);
            }
        });
    }
    // Format for display
    private formatValue(value: number): string {
        if (isNaN(value)) return '';
        if (this.style === 'percent') {
            value /= 100
        }
        return new Intl.NumberFormat('en-US', {
            style: this.style,
            minimumFractionDigits: 0,
            maximumFractionDigits: this.decimal,
        }).format(value);
    }

    private parseValue(value: string): number {
        const cleaned = value.replace(/,|%/g, '');
        return parseFloat(cleaned);
    }

    @HostListener('focus')
    onFocus() {
        // Show raw number on focus for easy editing
        const raw = this.parseValue(this.el.value);
        this.el.value = isNaN(raw) ? '' : String(raw);
    }

    @HostListener('blur')
    onBlur() {
        // Show formatted value on blur
        const raw = this.parseValue(this.el.value);
        console.log('Parsed value on blur:', raw);
        this.el.value = isNaN(raw) ? '' : this.formatValue(raw);
    }

    @HostListener('keydown', ['$event'])
    onKeyDown(e: KeyboardEvent) {
        const allowedKeys = [
            'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
            'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
            'Home', 'End'
        ];

        // Allow control keys
        if (allowedKeys.includes(e.key)) return;

        // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
        if (['a', 'c', 'v', 'x'].includes(e.key.toLowerCase()) && e.ctrlKey) return;

        // Allow decimal point (only one)
        if (e.key === '.' && !this.el.value.includes('.')) return;

        // Block negative numbers
        if (e.key === '-') {
            e.preventDefault();
            return;
        }

        // Allow only digits
        if (!/^\d$/.test(e.key)) {
            e.preventDefault();
        }
    }

    @HostListener('paste', ['$event'])
    onPaste(e: ClipboardEvent) {
        e.preventDefault();
        const pastedInput = e.clipboardData ? e.clipboardData.getData('text/plain') : '';
        const cleaned = pastedInput.replace(/[^0-9.]/g, '');
        const value = parseFloat(cleaned);
        if (!isNaN(value) && value >= 0) {
            this.el.value = this.formatValue(value);
        }
    }

}